import React, { useState, useEffect } from 'react';
import { DatePicker, Checkbox, Button, Flex } from 'antd';
import dayjs from 'dayjs';
import { mdiCalendarMonth } from '@mdi/js';
import Icon from '@mdi/react';
import './style.css';
import { useWidgetDate } from 'store/redux/states/dates/dates.hooks';
import useQueryParams from 'hooks/useQueryParams';
import { staticRanges } from './components/staticRanges';
import Ninja from '@ninja';

const DateRangePicker = (props) => {
  const { RangePicker } = DatePicker;
  const {
    value = [],
    date_key = 'global',
    onChange,
    globalDate = true,
    placeholder = ['Start date', 'End date'],
    variant = 'filled',
    showExpandDays = false,
    icon = true,
    disabledDate,
    ...rest
  } = props;
  const { date_from, date_to, setDate, date_range_key } = useWidgetDate(date_key);
  const { setUrlParams } = useQueryParams();
  const [changeGlobal, setChangeGlobal] = useState(globalDate);
  const [customLabel, setCustomLabel] = useState();
  const [formattedValue, setFormattedValue] = useState([
    date_from ? dayjs(date_from) : null,
    date_to ? dayjs(date_to) : null,
  ]);

  useEffect(() => {
    if (date_range_key) {
      const matchedPreset = staticRanges.find((preset) => preset.key === date_range_key);
      if (matchedPreset) {
        const [start, end] = matchedPreset.value;
        setFormattedValue([start, end]);
        setCustomLabel(matchedPreset.label);

        setDate({
          date_from: start.format('YYYY-MM-DD'),
          date_to: end.format('YYYY-MM-DD'),
          change_global: changeGlobal,
          date_key: date_key,
          label: matchedPreset.label,
          date_range_key: matchedPreset.key,
        });

        if (changeGlobal) {
          setUrlParams({
            date_from: start.format('YYYY-MM-DD'),
            date_to: end.format('YYYY-MM-DD'),
          });
        }
      }
    } else {
      setCustomLabel(null);
    }
  }, [date_range_key]);

  useEffect(() => {
    setFormattedValue([date_from ? dayjs(date_from) : null, date_to ? dayjs(date_to) : null]);
  }, [date_from, date_to]);

  useEffect(() => {
    if (date_from && date_to) {
      setFormattedValue([dayjs(date_from), dayjs(date_to)]);
    } else {
      setFormattedValue([null, null]);
    }
  }, [date_from, date_to]);

  const onRangeChange = (dates, dateStrings) => {
    const formattedDates = dates ? dates.map((date) => date?.format('YYYY-MM-DD')) : [null, null];

    const matchedPreset = staticRanges.find(
      (preset) =>
        preset.value[0].format('YYYY-MM-DD') === formattedDates[0] &&
        preset.value[1].format('YYYY-MM-DD') === formattedDates[1]
    );

    const dateLabel = matchedPreset ? matchedPreset.label : null;
    const dateKey = matchedPreset ? matchedPreset.key : null;
    setCustomLabel(matchedPreset?.label ?? null);

    setDate({
      date_from: formattedDates[0],
      date_to: formattedDates[1],
      change_global: changeGlobal,
      date_key: date_key,
      label: dateLabel,
      date_range_key: dateKey,
    });

    if (changeGlobal) {
      setUrlParams({ date_from: formattedDates[0], date_to: formattedDates[1] });
    }
  };

  const adjustDateRange = (type) => {
    if (!formattedValue[0] || !formattedValue[1]) return;

    let newStartDate = formattedValue[0];
    let newEndDate = formattedValue[1];

    if (type === 'subtract') {
      newStartDate = newStartDate.subtract(10, 'days');
    } else if (type === 'add') {
      newEndDate = newEndDate.add(10, 'days');
    }

    setFormattedValue([newStartDate, newEndDate]);
    onRangeChange([newStartDate, newEndDate]);
  };

  return (
    <Flex className='h-full' align='center' gap='small' wrap={true}>
      {showExpandDays && (
        <Button size='medium' shape='round' onClick={() => adjustDateRange('subtract')}>
          -10 Days
        </Button>
      )}
      <div className='relative w-full h-full max-w-fit'>
        <RangePicker
          style={{}}
          size='small'
          value={
            formattedValue[0] && formattedValue[1]
              ? [dayjs(formattedValue[0]), dayjs(formattedValue[1])]
              : [null, null]
          }
          format='MMM-DD'
          className='!text-sm !font-semibold w-full'
          {...rest}
          allowClear={false}
          presets={staticRanges}
          disabledDate={disabledDate}
          onChange={onRangeChange}
          separator='-'
          variant={variant}
          suffixIcon={icon && <Icon className='text-orange' path={mdiCalendarMonth} />}
          renderExtraFooter={() => (
            <Checkbox
              className='px-2 text-sm font-normal'
              onChange={(e) => setChangeGlobal(e.target.checked)}
              checked={changeGlobal}
            >
              Apply Globally
            </Checkbox>
          )}
        />
        {customLabel && (
          <Button
            size='medium'
            iconPosition='end'
            icon={<Icon color={Ninja.colors.orange} path={mdiCalendarMonth} />}
            className={`absolute inset-0 h-full flex items-center px-3 text-sm font-semibold pointer-events-none  ${
              variant === 'filled' ? 'ant-picker-filled' : ''
            }`}
          >
            {customLabel}
          </Button>
        )}
      </div>

      {showExpandDays && (
        <Button size='medium' shape='round' onClick={() => adjustDateRange('add')}>
          +10 Days
        </Button>
      )}
    </Flex>
  );
};

export default DateRangePicker;
